// Owner BI — unfinished-booking recovery per month (leads, converted, recovered € after a reminder).
import React, { useEffect, useState } from 'react';
import { Loader2, Undo2 } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (n) => `€${Math.round(Number(n) || 0).toLocaleString()}`;
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });

const Kpi = ({ label, value, sub, cls = 'bg-white', testId }) => (
  <div className={`${cls} border rounded-xl p-3`}>
    <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-slate-900" data-testid={testId}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const RecoveryStatsCard = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiCall(`/api/admin/booking-leads/stats?months=${months}`).then((d) => alive && setData(d)).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [months]);

  const t = data?.totals || {};
  const list = data?.months || [];
  const maxLeads = Math.max(1, ...list.map((m) => m.leads));
  const bars = list.map((m) => (
    <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-[36px]" data-testid={`recovery-bar-${m.month}`}>
      <div className="w-full h-24 flex items-end gap-0.5">
        <div className="flex-1 bg-slate-200 rounded-t" style={{ height: `${(m.leads / maxLeads) * 100}%` }} title={`${m.leads} leads`} />
        <div className="flex-1 bg-emerald-500 rounded-t" style={{ height: `${(m.converted / maxLeads) * 100}%` }} title={`${m.converted} converted`} />
      </div>
      <div className="text-[10px] text-slate-500">{monthLabel(m.month)}</div>
    </div>
  ));
  const rows = list.slice().reverse().map((m) => (
    <tr key={m.month} className="border-b last:border-0" data-testid={`recovery-row-${m.month}`}>
      <td className="py-1.5 px-2 font-semibold">{monthLabel(m.month)}</td>
      <td className="py-1.5 px-2 text-right">{m.leads}</td>
      <td className="py-1.5 px-2 text-right">{m.reminded}</td>
      <td className="py-1.5 px-2 text-right text-emerald-700 font-semibold">{m.converted} <span className="text-slate-400 font-normal">({m.conversion_rate}%)</span></td>
      <td className="py-1.5 px-2 text-right text-emerald-700 font-bold">{eur(m.recovered_eur)}</td>
      <td className="py-1.5 px-2 text-right">{eur(m.converted_eur)}</td>
      <td className="py-1.5 px-2 text-right text-slate-500">{m.lost} / {m.open}</td>
    </tr>
  ));
  const tableHead = (
    <thead className="bg-slate-50 text-slate-500">
      <tr><th className="text-left py-1.5 px-2">Month</th><th className="text-right py-1.5 px-2">Leads</th><th className="text-right py-1.5 px-2">Reminded</th><th className="text-right py-1.5 px-2">Booked</th><th className="text-right py-1.5 px-2">Won back</th><th className="text-right py-1.5 px-2">Booked value</th><th className="text-right py-1.5 px-2">Lost / open</th></tr>
    </thead>
  );

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="recovery-stats-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Undo2 className="w-4 h-4 text-emerald-600" /> Unfinished bookings won back</h3>
        <span className="text-xs text-slate-500">Guests who saved their dates but did not finish — and came back after a reminder.</span>
        <span className="flex-1" />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="border rounded-lg px-2 py-1 text-xs" data-testid="recovery-months">
          <option value={3}>3 months</option><option value={6}>6 months</option><option value={12}>12 months</option>
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Unfinished bookings" value={t.leads ?? '—'} sub={`${eur(t.estimate_eur)} at stake`} testId="recovery-kpi-leads" />
        <Kpi label="Came back & booked" value={t.converted ?? '—'} sub={`${t.conversion_rate ?? 0}% of leads`} cls="bg-emerald-50 border-emerald-200" testId="recovery-kpi-converted" />
        <Kpi label="Revenue won back" value={eur(t.recovered_eur)} sub={`${t.recovered ?? 0} booked after a reminder`} cls="bg-emerald-50 border-emerald-200" testId="recovery-kpi-recovered" />
        <Kpi label="Still open" value={t.open ?? '—'} sub={`${t.lost ?? 0} marked lost`} testId="recovery-kpi-open" />
      </div>
      <div className="flex gap-1 items-end" data-testid="recovery-bars">{bars}</div>
      <div className="text-[10px] text-slate-400 flex gap-3"><span><span className="inline-block w-2 h-2 bg-slate-200 rounded-sm mr-1" />leads</span><span><span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm mr-1" />booked</span></div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">{tableHead}<tbody>{rows}</tbody></table>
      </div>
    </div>
  );
};

export default RecoveryStatsCard;
